import { tryParseCustomAnalyticsChartOutput } from "@redotech/redo-analytics-common/reports/custom-analytics-chart";
import { ReactNode } from "react";
import {
  parseCapabilityBlockRenderData,
  SHOW_CAPABILITY_BLOCK_TOOL_NAME,
} from "../../capability-block-tool";
import {
  CapabilityBlockCard,
  CapabilityBlockFallbackCard,
} from "./capability-block-card";
import {
  ConciergeAutomationCard,
  ConciergeAutomationListCard,
  ConciergeConfigCard,
  ConciergeConfigUpdateCard,
  ConciergeConversationCard,
  ConciergeConversationListCard,
  ConciergeCustomerCard,
  ConciergeKnowledgeCard,
  ConciergeKnowledgeListCard,
} from "./concierge-cards";
import { CustomAnalyticsChartCard } from "./custom-analytics-chart-card";
import { InventoryCodeModeCards } from "./inventory-code-mode-cards";
import { OrderDocCard, OrderErrorCard } from "./order-summary-card";
import {
  tryParseConciergeAutomationDetail,
  tryParseConciergeAutomationList,
  tryParseConciergeConfig,
  tryParseConciergeConfigUpdate,
  tryParseConciergeConversationDetail,
  tryParseConciergeConversationList,
  tryParseConciergeCustomer,
  tryParseConciergeKnowledge,
  tryParseConciergeKnowledgeSearch,
} from "./parse-concierge";
import {
  formatAskUserQuestionResponse,
  tryParseInventoryCodeModeOutput,
} from "./parse-inventory-code-mode";
import { tryParseOrderDoc, tryParseOrderError } from "./parse-order-data";
import {
  tryParseReturnDisambiguation,
  tryParseReturnDoc,
  tryParseReturnError,
} from "./parse-return-data";
import {
  DisambiguationCard,
  ReturnDocCard,
  ReturnErrorCard,
} from "./return-summary-card";

/**
 * Shape-handler registry for per-tool rich output rendering.
 *
 * A single tool's output can come in several legal shapes (success,
 * error, disambiguation, etc.). Instead of one wrapper component
 * that probes the output internally and returns `null` on mismatch
 * — which is invisible to the caller, because the JSX wrapper is
 * still truthy even when the wrapped component renders nothing —
 * each shape registers a `(parse, render)` pair:
 *
 *   - `parse(output)` returns typed data or `null`.
 *   - `render(data)` receives the typed data and returns the card.
 *
 * The caller iterates the handlers in order, picks the first whose
 * parse succeeds, and renders it. If none match, the caller knows
 * to fall back to the generic tool-group line (because `null` is
 * a real signal, not a hidden empty JSX wrapper).
 *
 * Wins over the previous approach:
 *   - Single source of truth per shape (parser determines match
 *     AND extracts typed data — no double-probing).
 *   - Type-safe through the `T` generic — the render fn receives a
 *     fully-typed `data`, no `unknown` inside the card.
 *   - No silent failures from a renderer that "rendered" null.
 *   - Adding a new tool/shape is one line in the per-tool array.
 */

interface ShapeRenderer {
  renderIfMatched: (
    output: unknown,
    options: ToolShapeRenderOptions,
  ) => ReactNode | null;
}

export interface ToolShapeRenderOptions {
  disabled?: boolean;
  onSendMessage?: (text: string) => void;
}

/** Constructor helper — gives us inference on `T` without verbose
 *  generic annotations at the registry call sites. */
function shape<T>(
  parse: (output: unknown) => T | null,
  render: (data: T, options: ToolShapeRenderOptions) => ReactNode,
): ShapeRenderer {
  return {
    renderIfMatched: (output, options) => {
      const data = parse(output);
      return data === null ? null : render(data, options);
    },
  };
}

function renderInventoryCodeModeCards(
  data: NonNullable<ReturnType<typeof tryParseInventoryCodeModeOutput>>,
  options: ToolShapeRenderOptions,
): ReactNode {
  return (
    <InventoryCodeModeCards
      data={data}
      disabled={options.disabled}
      onQuestionResponse={
        options.onSendMessage
          ? (_toolCallId, response) =>
              options.onSendMessage?.(formatAskUserQuestionResponse(response))
          : undefined
      }
    />
  );
}

/** Per-tool list of shape handlers. The dispatcher walks the list
 *  in order; the first parser that succeeds wins. Order matters
 *  only when shapes can legitimately overlap — keep the most
 *  specific shape first. */
// The registry stores erased handlers. Type-safety lives at
// construction time (`shape<T>(...)`), where each render fn receives
// the exact type returned by its parser.
const TOOL_SHAPE_RENDERERS: Record<string, ShapeRenderer[]> = {
  execute_code: [
    shape(tryParseInventoryCodeModeOutput, renderInventoryCodeModeCards),
  ],
  "ask-user-question": [
    shape(tryParseInventoryCodeModeOutput, renderInventoryCodeModeCards),
  ],
  get_return_data: [
    shape(tryParseReturnDoc, (doc) => <ReturnDocCard doc={doc} />),
    shape(tryParseReturnError, (err) => <ReturnErrorCard error={err.error} />),
    shape(tryParseReturnDisambiguation, (data) => (
      <DisambiguationCard data={data} />
    )),
  ],
  get_order: [
    shape(tryParseOrderDoc, (doc) => <OrderDocCard doc={doc} />),
    shape(tryParseOrderError, (err) => <OrderErrorCard error={err.error} />),
  ],
  "get-commerce-agent-config": [
    shape(tryParseConciergeConfig, (data) => (
      <ConciergeConfigCard data={data} />
    )),
  ],
  "set-commerce-agent-config": [
    shape(tryParseConciergeConfigUpdate, (data) => (
      <ConciergeConfigUpdateCard data={data} />
    )),
  ],
  "list-commerce-agent-automations": [
    shape(tryParseConciergeAutomationList, (data) => (
      <ConciergeAutomationListCard data={data} />
    )),
  ],
  "get-commerce-agent-automation": [
    shape(tryParseConciergeAutomationDetail, (data) => (
      <ConciergeAutomationCard data={data} />
    )),
  ],
  "list-support-v2-conversations": [
    shape(tryParseConciergeConversationList, (data) => (
      <ConciergeConversationListCard data={data} />
    )),
  ],
  "get-support-v2-conversation": [
    shape(tryParseConciergeConversationDetail, (data) => (
      <ConciergeConversationCard data={data} />
    )),
  ],
  "get-commerce-agent-customer": [
    shape(tryParseConciergeCustomer, (data) => (
      <ConciergeCustomerCard data={data} />
    )),
  ],
  "get-knowledge": [
    shape(tryParseConciergeKnowledge, (data) => (
      <ConciergeKnowledgeCard data={data} />
    )),
  ],
  "search-knowledge": [
    shape(tryParseConciergeKnowledgeSearch, (data) => (
      <ConciergeKnowledgeListCard data={data} />
    )),
  ],
  "generate-custom-analytics-chart": [
    shape(tryParseCustomAnalyticsChartOutput, (chart) => (
      <CustomAnalyticsChartCard chart={chart} />
    )),
  ],
  [SHOW_CAPABILITY_BLOCK_TOOL_NAME]: [
    shape(parseCapabilityBlockRenderData, (data) =>
      data.kind === "presentation" ? (
        <CapabilityBlockCard data={data.presentation} />
      ) : (
        <CapabilityBlockFallbackCard />
      ),
    ),
  ],
  // Add new entries here as more tools get rich renderers.
  // Pattern: one shape() per legitimate output shape; if any
  // parser matches, the corresponding card renders.
};

/**
 * Resolve an `output-available` tool part to its inline-promoted
 * card, or `null` if there's no renderer for this tool / no shape
 * matched the output. `null` is a real "no card" signal — callers
 * can rely on it to fall through to the generic tool-group row.
 */
export function tryRenderInlineToolOutput(
  toolName: string,
  state: string,
  output: unknown,
  options: ToolShapeRenderOptions = {},
): ReactNode | null {
  if (
    toolName === SHOW_CAPABILITY_BLOCK_TOOL_NAME &&
    state === "output-error"
  ) {
    return <CapabilityBlockFallbackCard />;
  }
  if (state !== "output-available") {
    return null;
  }
  const handlers = TOOL_SHAPE_RENDERERS[toolName];
  if (!handlers) {
    return null;
  }
  for (const handler of handlers) {
    const rendered = handler.renderIfMatched(output, options);
    if (rendered !== null) {
      return rendered;
    }
  }
  return null;
}
